import {
  lineItemResolutionSchema,
  type LineItemResolutionFormValues,
} from "@babackapp/return-widget-components/forms/line-item-resolution.form";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useAvailableResolutions } from "@babackapp/return-widget-components/hooks/use-available-resolutions";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import type {
  OrderLineItem,
  Resolution,
  ResolutionOption,
} from "@babackapp/return-widget-components/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentOrderLineItems } from "./order";
import { useWidgetParams } from "./use-widget-params";

export type ResolutionOptionsFormContext =
  | { kind: "refund-or-discount" }
  | { kind: "exchange" };

export function useResolutionOptionsForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId } = useWidgetParams();
  const draft = useDraftReturn(orderId);
  const updateLineItem = useUpdateLineItem(orderId);
  const { data: lineItems = [] } = useCurrentOrderLineItems();
  const { data: order } = usePublicOrder(orderId);

  const lineItem = lineItems.find((li) => li.line_item_id === lineItemId);
  // variantByLineItemId values are untyped in the generated schema (Record<string, unknown>)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const variantCollection = (order?.variantByLineItemId?.[lineItemId] ?? null) as any;
  const availableResolutions = useAvailableResolutions(
    shopId,
    lineItem ?? ({} as OrderLineItem),
    variantCollection,
  );
  const exchangeAvailable = availableResolutions.includes("exchange");
  const exchangeCreditAvailable = availableResolutions.includes("exchange-credit");
  const discountAvailable = availableResolutions.includes("discount");
  const refundAvailable = availableResolutions.includes("refund");

  const draftLineItem = draft?.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );

  const resolution: Resolution | null = draftLineItem?.resolution ?? null;

  const isExchange =
    resolution === "exchange" || resolution === "exchange-credit";

  const context: ResolutionOptionsFormContext = isExchange
    ? { kind: "exchange" }
    : { kind: "refund-or-discount" };

  const defaultResolution = isExchange
    ? (resolution ?? (exchangeAvailable ? "exchange" : "exchange-credit"))
    : (resolution ?? "refund");

  const form = useForm<LineItemResolutionFormValues>({
    resolver: zodResolver(lineItemResolutionSchema),
    defaultValues: {
      resolution: defaultResolution,
      resolutionOption:
        (draftLineItem?.resolutionOption as ResolutionOption | null) ??
        (isExchange ? null : discountAvailable ? "discount-code" : "bank-transfer"),
      exchangeVariantId: draftLineItem?.exchangeVariantId ?? null,
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (context.kind === "exchange") {
      const res = values.resolution as "exchange" | "exchange-credit";
      updateLineItem(lineItemId, {
        resolution: res,
        exchangeVariantId: res === "exchange" ? values.exchangeVariantId : null,
      });
    } else {
      updateLineItem(lineItemId, {
        resolution:
          values.resolutionOption === "discount-code" ? "discount" : "refund",
        resolutionOption: values.resolutionOption,
      });
    }
    navigate("../../../summary");
  });

  return {
    form,
    onSubmit,
    updateLineItem,
    lineItemId,
    orderId,
    shopId,
    resolution,
    context,
    exchangeAvailable,
    exchangeCreditAvailable,
    discountAvailable,
    refundAvailable,
  };
}
